import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { legalFoundationService } from '@/lib/api/services/legalFoundation'
import type {
  LegalFoundation,
  LegalFoundationCategory,
  LegalFoundationFilterParams,
  LegalFoundationStats,
  ApiResponse,
  PaginatedResponse,
  PaginationParams,
  CreateLegalFoundation,
  UpdateLegalFoundation
} from '@/lib/api/types'

// Query keys
export const legalFoundationKeys = {
  all: ['legalFoundations'] as const,
  lists: () => [...legalFoundationKeys.all, 'list'] as const,
  list: (params: PaginationParams, filters: LegalFoundationFilterParams) =>
    [...legalFoundationKeys.lists(), params, filters] as const,
  search: (query: string, params: PaginationParams) => [...legalFoundationKeys.lists(), 'search', query, params] as const,
  details: () => [...legalFoundationKeys.all, 'detail'] as const,
  detail: (id: string) => [...legalFoundationKeys.details(), id] as const,
  stats: () => [...legalFoundationKeys.all, 'stats'] as const,
  categories: () => [...legalFoundationKeys.all, 'categories'] as const,
  highlights: (type: string) => [...legalFoundationKeys.all, 'highlights', type] as const,
}

// Hook to fetch legalFoundations with pagination and filters
export function useLegalFoundations(
  params: PaginationParams = { page: 1, size: 50 },
  filters: LegalFoundationFilterParams = {},
  enabled: boolean = true
) {
  return useQuery<ApiResponse<PaginatedResponse<LegalFoundation>>, Error>({
    queryKey: legalFoundationKeys.list(params, filters),
    queryFn: () => legalFoundationService.getLegalFoundations(params, filters),
    enabled,
  })
}

// Hook to fetch legalFoundation stats
export function useLegalFoundationStats() {
  return useQuery<LegalFoundationStats, Error>({
    queryKey: legalFoundationKeys.stats(),
    queryFn: async () => {
      const response = await legalFoundationService.getLegalFoundationStats()
      return response.data
    },
  })
}

// Hook to fetch a single legalFoundation by ID
export function useLegalFoundation(legalFoundationId: string) {
  return useQuery<ApiResponse<LegalFoundation>, Error>({
    queryKey: legalFoundationKeys.detail(legalFoundationId),
    queryFn: () => legalFoundationService.getLegalFoundationById(legalFoundationId),
    enabled: !!legalFoundationId,
    staleTime: 5 * 60 * 1000 // 5 minutes
  })
}

// Hook to create a legalFoundation
export function useCreateLegalFoundation() {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<LegalFoundation>, Error, CreateLegalFoundation>({
    mutationFn: (data: CreateLegalFoundation) =>
      legalFoundationService.createLegalFoundation(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: legalFoundationKeys.lists() })
      queryClient.invalidateQueries({ queryKey: legalFoundationKeys.stats() })
    },
  })
}

// Hook to update a legalFoundation
export function useUpdateLegalFoundation() {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<LegalFoundation>, Error, { legalFoundationId: string; data: UpdateLegalFoundation }>({
    mutationFn: ({ legalFoundationId, data }) =>
      legalFoundationService.updateLegalFoundation(legalFoundationId, data),
    onSuccess: (_, { legalFoundationId }) => {
      queryClient.invalidateQueries({ queryKey: legalFoundationKeys.detail(legalFoundationId) })
      queryClient.invalidateQueries({ queryKey: legalFoundationKeys.lists() })
      queryClient.invalidateQueries({ queryKey: legalFoundationKeys.stats() })
    },
  })
}

// Hook to update legalFoundation status
export function useUpdateLegalFoundationStatus() {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<LegalFoundation>, Error, { legalFoundationId: string; status: 'DRAFT' | 'PUBLISH' }>({
    mutationFn: ({ legalFoundationId, status }) =>
      legalFoundationService.updateLegalFoundationStatus(legalFoundationId, status),
    onSuccess: (response, { legalFoundationId }) => {
      if (response.success && response.data) {
        queryClient.setQueryData(legalFoundationKeys.detail(legalFoundationId), response)
      }
      queryClient.invalidateQueries({ queryKey: legalFoundationKeys.lists() })
      queryClient.invalidateQueries({ queryKey: legalFoundationKeys.stats() })
    },
  })
}

// Hook to delete a legalFoundation
export function useDeleteLegalFoundation() {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<null>, Error, string>({
    mutationFn: (legalFoundationId: string) =>
      legalFoundationService.deleteLegalFoundation(legalFoundationId),
    onSuccess: () => {
      // Invalidate and refetch legalFoundations list
      queryClient.invalidateQueries({ queryKey: legalFoundationKeys.lists() })
      queryClient.invalidateQueries({ queryKey: legalFoundationKeys.stats() })
    },
  })
}

// Hook to fetch legalFoundation categories
export function useLegalFoundationCategories() {
  return useQuery<ApiResponse<LegalFoundationCategory[]>, Error>({
    queryKey: legalFoundationKeys.categories(),
    queryFn: () => legalFoundationService.getLegalFoundationCategories(),
  })
}

// Hook to search legalFoundations
export function useSearchLegalFoundations(
  params: PaginationParams = { page: 1, size: 10 },
  searchBy?: string,
  enabled: boolean = true
) {
  return useQuery<ApiResponse<PaginatedResponse<LegalFoundation>>, Error>({
    queryKey: legalFoundationKeys.search(searchBy || '', params),
    queryFn: () => legalFoundationService.searchLegalFoundations(params, searchBy),
    enabled: enabled && !!searchBy && searchBy.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache for 10 minutes
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: false,
    refetchOnMount: false
  })
}

// Hook to fetch legalFoundation highlights (Key Resources)
export function useLegalFoundationHighlights(type: string = 'HOME_PAGE_KEY_RESOURCES') {
  return useQuery<ApiResponse<LegalFoundation[]>, Error>({
    queryKey: legalFoundationKeys.highlights(type),
    queryFn: () => legalFoundationService.getLegalFoundationHighlights(type),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

// Hook to update legalFoundation highlights (Key Resources)
export function useUpdateLegalFoundationHighlights() {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<null>, Error, { legalFoundationIds: string[], type?: string }>({
    mutationFn: ({ legalFoundationIds, type = 'HOME_PAGE_KEY_RESOURCES' }) =>
      legalFoundationService.updateLegalFoundationHighlights(legalFoundationIds, type),
    onSuccess: () => {
      // Invalidate the highlights query to refetch
      queryClient.invalidateQueries({ queryKey: legalFoundationKeys.all })
    },
  })
}
